import { useState, useEffect, useRef } from 'react';
import { submitAttempt } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './CipherForge.css';

const MAX_REVEALS = 6;
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};

export default function CipherForge({ puzzle, onSolve, alreadyAttempted, onPlayAgain, loadingPlayAgain }) {
  const { user, fetchCredits } = useAuth();

  const [userInput, setUserInput]       = useState({});
  const [selectedEnc, setSelectedEnc]   = useState(null); // which encoded letter is selected
  const [revealsUsed, setRevealsUsed]   = useState(0);
  const [gameOver, setGameOver]         = useState(false);
  const [correct, setCorrect]           = useState(false);
  const [shareText, setShareText]       = useState('');
  const [elapsed, setElapsed]           = useState(0);

  const startTime = useRef(null);
  const timerRef  = useRef(null);

  useEffect(() => {
    if (!puzzle?.id) return;
    const key = `cf_start_${puzzle.id}`;
    if (!sessionStorage.getItem(key)) sessionStorage.setItem(key, Date.now().toString());
    startTime.current = parseInt(sessionStorage.getItem(key));
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime.current) / 1000));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [puzzle?.id]);

  useEffect(() => {
    if (gameOver) clearInterval(timerRef.current);
  }, [gameOver]);

  useEffect(() => {
    setUserInput({});
    setSelectedEnc(null);
    setRevealsUsed(0);
    setGameOver(false);
    setCorrect(false);
    setShareText('');
    setElapsed(0);
  }, [puzzle?.id]);

  if (!puzzle?.content) return <div className="cf-loading mono">Loading puzzle...</div>;
  if (loadingPlayAgain) return (
    <div className="cf-loading mono" style={{textAlign:'center', padding:'40px 20px'}}>
      <div style={{fontSize:'32px', marginBottom:'12px'}}>⏳</div>
      <div>✨ Generating new puzzle powered by AI</div>
    </div>
  );
  if (alreadyAttempted) return (
    <div className="cf-loading mono" style={{textAlign:'center', padding:'40px 20px'}}>
      <div style={{fontSize:'32px', marginBottom:'12px'}}><span style={{color:'initial'}}>✅</span></div>
      <div style={{fontSize:'16px', fontWeight:'700', color:'#f0f4ff', marginBottom:'8px'}}>Already completed today!</div>
      <div style={{fontSize:'12px', color:'rgba(240,244,255,0.5)', marginBottom:'16px'}}>Use 1 credit to play a new puzzle.</div>
      {onPlayAgain
        ? <button onClick={onPlayAgain} style={{fontSize:'12px', color:'#4fe3c1', background:'rgba(79,227,193,0.1)', border:'1px solid rgba(79,227,193,0.35)', borderRadius:'8px', padding:'8px 16px', cursor:'pointer'}}>✨ Play again — 1 credit</button>
        : <a href="/upgrade" style={{fontSize:'12px', color:'#e8c547', textDecoration:'none'}}>Get more credits →</a>
      }
    </div>
  );

  const { encoded, message: originalMessage, backstory, reveals, theme, theme_emoji, difficulty } = puzzle.content;

  // Build revealed mappings
  const revealedMap = {};
  for (let i = 0; i < revealsUsed; i++) {
    const r = reveals[i];
    if (r) revealedMap[r.encoded] = r.decoded;
  }

  // Unique encoded letters in order of appearance
  const encodedLetters = [...new Set(encoded.replace(/[^A-Z]/g, '').split(''))];

  // Build decoded string
  const buildDecoded = () => {
    return encoded.split('').map(ch => {
      if (!ch.match(/[A-Z]/)) return ch;
      if (revealedMap[ch]) return revealedMap[ch];
      return userInput[ch] || '_';
    }).join('');
  };

  const decoded = buildDecoded();
  const allFilled = !decoded.includes('_');

  // Letters already used as decoded values (to prevent duplicates)
  const usedDecoded = new Set([
    ...Object.values(userInput),
    ...Object.values(revealedMap)
  ]);

  const handleSelectEnc = (enc) => {
    if (gameOver || revealedMap[enc]) return;
    setSelectedEnc(prev => prev === enc ? null : enc);
  };

  const handleSelectDecoded = (dec) => {
    if (!selectedEnc || gameOver) return;
    setUserInput(prev => {
      const next = { ...prev };
      // Remove this decoded letter from any other mapping
      Object.keys(next).forEach(k => { if (next[k] === dec) delete next[k]; });
      next[selectedEnc] = dec;
      return next;
    });
    // Auto-advance to next unmapped letter
    const currentIdx = encodedLetters.indexOf(selectedEnc);
    const nextUnmapped = encodedLetters.find((l, i) =>
      i > currentIdx && !revealedMap[l] && userInput[l] !== dec
    ) || encodedLetters.find(l => !revealedMap[l] && l !== selectedEnc && !userInput[l]);
    setSelectedEnc(nextUnmapped || null);
  };

  const handleClearEnc = (enc) => {
    if (gameOver || revealedMap[enc]) return;
    setUserInput(prev => { const next = { ...prev }; delete next[enc]; return next; });
  };

  const handleReveal = () => {
    if (revealsUsed >= MAX_REVEALS || gameOver) return;
    setRevealsUsed(r => r + 1);
    setSelectedEnc(null);
  };

  const handleCheck = () => {
    if (!allFilled || gameOver) return;
    const isCorrect = decoded.replace(/\s/g, '') === originalMessage.replace(/\s/g, '');
    setCorrect(isCorrect);
    setGameOver(true);
    const timeTakenCF = Math.floor((Date.now() - startTime.current) / 1000);
    const mins = Math.floor(timeTakenCF / 60); const secs = timeTakenCF % 60;
    const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    const dots = Array.from({length: MAX_REVEALS}).map((_, i) => i < revealsUsed ? '🟡' : '⬜').join('');
    const th = theme_emoji && theme ? `${theme_emoji} ${theme} Week\n` : '';
    setShareText(`${th}🔐 CipherForge\n${isCorrect ? `Decoded · ${revealsUsed} reveals · ${timeStr} ✅` : `Not decoded · ${timeStr} ❌`}\n${dots}\n\n🎮 Play today → puzzleforge.io`);
    if (user && puzzle) {
      submitAttempt({
        puzzle_id: puzzle.id,
        solved: isCorrect,
        num_guesses: revealsUsed,
        guesses: [],
        score: isCorrect ? Math.max(10, 100 - revealsUsed * 15) : 0,
        time_taken_seconds: timeTakenCF,
      }).then(() => { if (fetchCredits) fetchCredits(); }).catch(console.error);
    }
    if (isCorrect && onSolve) onSolve(isCorrect);
  };

  return (
    <div className="cf-wrap">

      {/* ── Header ── */}
      <div className="cf-header">
        <div className="cf-label">
          <span className="cf-emoji">🔐</span>
          <div>
            <span className="cf-tag mono">{theme_emoji} {theme}</span>
            <span className="cf-title mono">CipherForge</span>
          </div>
        </div>
        <div className="cf-header-right">
          <span className="cf-timer mono">⏱ {formatTime(elapsed)}</span>
          <span className="cf-reveals-left mono">{MAX_REVEALS - revealsUsed} 💡</span>
        </div>
      </div>

      {/* ── Backstory ── */}
      <div className="cf-backstory mono">
        <span className="cf-backstory-icon">📜</span>
        {backstory}
      </div>

      {/* ── Encoded message — tappable letters ── */}
      <div className="cf-section">
        <div className="cf-section-label mono">Tap a letter to decode it:</div>
        <div className="cf-encoded-wrap">
          {encoded.split('').map((ch, i) => {
            if (!ch.match(/[A-Z]/)) return <span key={i} className="cf-space">{ch === ' ' ? ' ' : ch}</span>;
            const isRevealed = !!revealedMap[ch];
            const isMapped = !!userInput[ch];
            const isSelected = selectedEnc === ch;
            const displayChar = revealedMap[ch] || userInput[ch] || null;
            return (
              <div
                key={i}
                className={`cf-letter-block ${isSelected ? 'selected' : ''} ${isRevealed ? 'revealed' : ''} ${isMapped ? 'mapped' : ''}`}
                onClick={() => !gameOver && handleSelectEnc(ch)}
              >
                <span className="cf-letter-decoded mono">{displayChar || '?'}</span>
                <span className="cf-letter-encoded mono">{ch}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Alphabet picker ── */}
      {!gameOver && selectedEnc && (
        <div className="cf-picker">
          <div className="cf-picker-label mono">
            Map <strong>{selectedEnc}</strong> →
            {userInput[selectedEnc] && (
              <button className="cf-picker-clear mono" onClick={() => handleClearEnc(selectedEnc)}>
                ✕ clear
              </button>
            )}
          </div>
          <div className="cf-alphabet">
            {ALPHABET.map(letter => {
              const isUsed = usedDecoded.has(letter) && userInput[selectedEnc] !== letter;
              const isChosen = userInput[selectedEnc] === letter;
              return (
                <button
                  key={letter}
                  className={`cf-alpha-btn mono ${isChosen ? 'chosen' : ''} ${isUsed ? 'used' : ''}`}
                  onClick={() => !isUsed && handleSelectDecoded(letter)}
                  disabled={isUsed}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Reveals ── */}
      {!gameOver && (
        <div className="cf-reveals">
          <div className="cf-reveals-dots">
            {Array.from({length: MAX_REVEALS}).map((_, i) => (
              <div key={i} className={`cf-reveal-dot ${i < revealsUsed ? 'used' : ''}`} />
            ))}
          </div>
          <button className="cf-reveal-btn mono" onClick={handleReveal} disabled={revealsUsed >= MAX_REVEALS}>
            💡 {revealsUsed >= MAX_REVEALS ? 'No more reveals' : `Reveal most common letter (${MAX_REVEALS - revealsUsed} left)`}
          </button>
        </div>
      )}

      {/* ── Result ── */}
      {gameOver && (
        <div className={`cf-result ${correct ? 'correct' : 'wrong'}`}>
          <div className="cf-result-icon">{correct ? '🎉' : '😔'}</div>
          <div className="cf-result-text mono">
            {correct
              ? `Decoded in ${revealsUsed} reveals · ${formatTime(elapsed)}`
              : `The message was: ${originalMessage}`}
          </div>
          {shareText && (
            <button className="cf-share mono" onClick={() => navigator.clipboard.writeText(shareText).catch(() => {})}>
              📋 Copy Result
            </button>
          )}
        </div>
      )}

      {/* ── Submit ── */}
      {!gameOver && (
        <button className="cf-submit mono" onClick={handleCheck} disabled={!allFilled}>
          {allFilled ? 'Check Decoding →' : `${decoded.split('').filter(c => c === '_').length} letters left to map`}
        </button>
      )}

    </div>
  );
}
